import { CdkDrag, CdkDragMove } from '@angular/cdk/drag-drop';
import { Component, ElementRef, input, output, signal, viewChild } from '@angular/core';
import { delay } from '@gametailors/web-core/misc';

@Component({
  selector: 'rid-draggable',
  imports: [CdkDrag],
  templateUrl: './rid-draggable.html',
})
export class RidDraggable {
  grabbable = input<boolean>(true);
  /** Whether the built-in hover/drag rotate+scale+shadow feedback plays. Off for consumers that fully own their own reward-style feedback (e.g. the scrapbook), on by default for game pieces that want the generic "juice". */
  juice = input<boolean>(true);

  isDragging = signal(false);
  isSnapped = signal(false);
  snapTransition = signal<'none' | 'ease-out' | 'ease-out-back'>('none');

  draggable = viewChild.required<CdkDrag>('draggable');
  wrapper = viewChild.required<ElementRef<HTMLElement>>('wrapper');

  offset = signal({ x: 0, y: 0 });
  currentCursorOffset = signal({ x: 0, y: 0 });

  started = output<RidDraggable>();
  moved = output<[RidDraggable, { x: number; y: number }]>();
  released = output<RidDraggable>();

  // NOTE: `#wrapper` never itself gets translated (only its child `#el` does), so its
  // resting center can always be read fresh via getBoundingClientRect() — no need to cache
  // it via Resize/IntersectionObserver, which can silently never fire.
  private getOrigin(): { x: number; y: number } {
    const rect = this.wrapper().nativeElement.getBoundingClientRect();
    return { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 };
  }

  onDragStarted() {
    this.isDragging.set(true);
    this.started.emit(this);
  }

  onDragMoved(event: CdkDragMove) {
    const point = event.pointerPosition;
    const origin = this.getOrigin();
    const cursorOffset = {
      x: point.x - origin.x,
      y: point.y - origin.y,
    };

    // Always track cursor position
    this.currentCursorOffset.set(cursorOffset);

    // Only update offset when not snapped (following cursor)
    if (!this.isSnapped()) {
      this.offset.set(cursorOffset);
    }
    this.moved.emit([this, event.pointerPosition]);
  }

  private skipSnapBack = false;

  onDragReleased() {
    this.isDragging.set(false);
    this.isSnapped.set(false);
    this.released.emit(this);

    if (this.skipSnapBack) {
      this.skipSnapBack = false;
    } else {
      this.snapToOrigin();
    }
  }

  async snapToOrigin() {
    await this.moveTo(0, 0, 'ease-out-back');

    this.draggable().reset();
  }

  private resetInstantly() {
    this.skipSnapBack = true;
    this.isDragging.set(false);
    this.isSnapped.set(false);
    this.snapTransition.set('none');
    this.offset.set({ x: 0, y: 0 });
    this.draggable().reset();
  }

  private animateRespawn() {
    requestAnimationFrame(() => {
      const el = this.wrapper().nativeElement;
      el.classList.remove('anim-pop-in');
      void el.offsetHeight;
      el.classList.add('anim-pop-in');
    });
  }

  respawn() {
    this.resetInstantly();
    this.animateRespawn();
  }

  async snapTo(x: number, y: number) {
    this.isSnapped.set(true);
    const origin = this.getOrigin();
    await this.moveTo(x - origin.x, y - origin.y, 'ease-out');
  }

  unsnap() {
    this.isSnapped.set(false);
    this.offset.set(this.currentCursorOffset());
  }

  private async moveTo(x: number, y: number, ease: 'ease-out' | 'ease-out-back') {
    this.snapTransition.set(ease);
    this.offset.set({ x, y });
    await delay(300);
    this.snapTransition.set('none');
  }
}
